import dcrfClient from 'dcrf-client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  addOperationalBreadcrumb,
  captureFrontendException,
  createOperationalError,
  setWebSocketScopeContext,
} from '../observability/sentry';
import { logTenantMetricsDebug } from '../utils/tenantMetricsDebug';
import { useAuth } from './AuthContext';
import { useOperationalTab } from './OperationalTabContext';

interface WebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

interface PaginateParams {
  pager?: {
    page?: number;
    pageSize?: number;
    reverse?: boolean;
    afterCursor?: string;
    beforeCursor?: string;
    useKeyset?: boolean;
  };
  filters?: Record<string, unknown>;
  exclude?: Record<string, unknown>;
  search?: {
    query?: string;
    lookups?: string[];
  };
  order?: string[];
}

interface ObserverOptions {
  includeCreateEvents?: boolean;
  includeUpdateEvents?: boolean;
  includeDeleteEvents?: boolean;
  subscribeAction?: string;
  unsubscribeAction?: string;
}

type ObserverCallback<TData = unknown> = (data: TData, action: string) => void;

interface WebSocketSubscription {
  unsubscribe?: () => void;
}

interface Observer {
  id: string;
  stream: string;
  target: string | number | object;
  callback: ObserverCallback;
  options?: ObserverOptions;
  subscriptionKey: string;
}

interface SharedObserverSubscription {
  key: string;
  stream: string;
  target: string | number | object;
  options?: ObserverOptions;
  callbacks: Map<string, ObserverCallback>;
  subscription?: WebSocketSubscription;
}

interface WebSocketContextType {
  state: WebSocketState;
  client: ReturnType<typeof dcrfClient.connect> | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  subscribe: <TData = unknown>(
    stream: string,
    target: string | number | object,
    callback: ObserverCallback<TData>,
    options?: Record<string, unknown>,
  ) => WebSocketSubscription;
  request: <TResponse = unknown>(
    stream: string,
    payload: Record<string, unknown>,
  ) => Promise<TResponse>;
  paginate: <TResponse = unknown>(
    stream: string,
    params?: PaginateParams,
  ) => Promise<TResponse>;
  addObserver: <TData = unknown>(
    stream: string,
    target: string | number | object,
    callback: ObserverCallback<TData>,
    options?: ObserverOptions,
  ) => string;
  removeObserver: (observerId: string) => void;
  clearObservers: () => void;
}

function cloneSharedObserverSubscriptions(
  source: Map<string, SharedObserverSubscription>,
): Map<string, SharedObserverSubscription> {
  return new Map(
    Array.from(source.entries()).map(([key, sharedSubscription]) => [
      key,
      {
        ...sharedSubscription,
        callbacks: new Map(sharedSubscription.callbacks),
        subscription: undefined,
      },
    ]),
  );
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

type WebSocketTransport = {
  on?: (event: string, handler: () => void) => void;
  off?: (event: string, handler: () => void) => void;
  removeListener?: (event: string, handler: () => void) => void;
  isConnected?: () => boolean;
};

const WAKE_UP_FORCE_RECONNECT_DEBOUNCE_MS = 5000;

function stableSerializeObserverValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerializeObserverValue(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableSerializeObserverValue(entryValue)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value);
}

function getObserverSubscriptionKey(
  stream: string,
  target: string | number | object,
  options?: ObserverOptions,
): string {
  return `${stream}:${stableSerializeObserverValue(target)}:${stableSerializeObserverValue(options ?? null)}`;
}

/**
 * Provides a shared DCRF WebSocket client and observer helpers.
 */
export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const { canOperateInThisTab } = useOperationalTab();
  const [state, setState] = useState<WebSocketState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });
  const stateRef = useRef<WebSocketState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });
  const clientRef = useRef<ReturnType<typeof dcrfClient.connect> | null>(null);
  const clientTokenRef = useRef<string | null>(null);
  const observersRef = useRef<Map<string, Observer>>(new Map());
  const sharedSubscriptionsRef = useRef<Map<string, SharedObserverSubscription>>(new Map());
  const transportListenersRef = useRef<{
    transport: WebSocketTransport;
    onConnect: () => void;
    onReconnect: () => void;
    onOpen: () => void;
  } | null>(null);
  const backgroundedAtRef = useRef<number | null>(null);
  const backgroundedReasonRef = useRef<string | null>(null);
  const lastWakeReconnectAtRef = useRef<number>(0);

  const wsUrl = (import.meta.env.VITE_WS_URL as string | undefined) || 'wss://dev.service.contact-tel.contactpro.com.br/ws';
  const logWebSocketDebug = useCallback((message: string, data?: unknown) => {
    logTenantMetricsDebug('WebSocketContext', message, data);
  }, []);
  const markPageBackgrounded = useCallback((reason: string) => {
    if (backgroundedAtRef.current !== null) {
      logWebSocketDebug('page background already marked', {
        reason,
        existingReason: backgroundedReasonRef.current,
        backgroundedAt: backgroundedAtRef.current,
      });
      return;
    }

    backgroundedAtRef.current = Date.now();
    backgroundedReasonRef.current = reason;
    logWebSocketDebug('page background marked for forced reconnect on wake-up', {
      reason,
      backgroundedAt: backgroundedAtRef.current,
    });
  }, [logWebSocketDebug]);

  const syncSentryWebSocketContext = useCallback((nextState?: WebSocketState) => {
    const effectiveState = nextState ?? stateRef.current;
    setWebSocketScopeContext({
      isConnected: effectiveState.isConnected,
      isConnecting: effectiveState.isConnecting,
      error: effectiveState.error,
      observerCount: observersRef.current.size,
      sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
      canOperateInThisTab,
    });
  }, [canOperateInThisTab]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const ensureClient = useCallback(() => {
    if (!clientRef.current) {
      logWebSocketDebug('ensureClient failed because no active client exists', {
        isConnected: state.isConnected,
        isConnecting: state.isConnecting,
        error: state.error,
        observerCount: observersRef.current.size,
        sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        canOperateInThisTab,
      });
      addOperationalBreadcrumb({
        category: 'websocket.client',
        message: 'ensureClient failed because the transport is disconnected',
        level: 'warning',
        data: {
          isConnected: state.isConnected,
          isConnecting: state.isConnecting,
          error: state.error,
          observerCount: observersRef.current.size,
          sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          canOperateInThisTab,
        },
      });
      throw createOperationalError('WebSocket nao esta conectado', {
        name: 'OperationalWebSocketDisconnectedError',
        fingerprint: ['websocket-not-connected'],
        tags: {
          operational_surface: 'websocket',
        },
        contexts: {
          websocket_operation: {
            is_connected: state.isConnected,
            is_connecting: state.isConnecting,
            error: state.error,
            observer_count: observersRef.current.size,
            shared_subscription_count: sharedSubscriptionsRef.current.size,
            can_operate_in_this_tab: canOperateInThisTab,
          },
        },
      });
    }
    return clientRef.current;
  }, [canOperateInThisTab, logWebSocketDebug, state.error, state.isConnected, state.isConnecting]);

  const isTransportConnected = useCallback((client: ReturnType<typeof dcrfClient.connect> | null = clientRef.current) => {
    const transport = (client as { transport?: WebSocketTransport } | null)?.transport;
    if (!transport?.isConnected) {
      return false;
    }
    try {
      return transport.isConnected();
    } catch {
      return false;
    }
  }, []);

  const clearTransportListeners = useCallback(() => {
    const current = transportListenersRef.current;
    if (!current) {
      return;
    }
    const { transport, onConnect, onReconnect, onOpen } = current;
    if (transport.off) {
      transport.off('connect', onConnect);
      transport.off('reconnect', onReconnect);
      transport.off('open', onOpen);
    } else if (transport.removeListener) {
      transport.removeListener('connect', onConnect);
      transport.removeListener('reconnect', onReconnect);
      transport.removeListener('open', onOpen);
    }
    transportListenersRef.current = null;
  }, []);

  const rebuildSharedSubscriptions = useCallback(
    (client: ReturnType<typeof dcrfClient.createClient> | null = clientRef.current) => {
      if (!client) {
        logWebSocketDebug('rebuildSharedSubscriptions skipped because client is missing');
        return;
      }

      logWebSocketDebug('rebuildSharedSubscriptions starting', {
        sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        observerCount: observersRef.current.size,
      });
      sharedSubscriptionsRef.current.forEach((sharedSubscription, subscriptionKey) => {
        const callbacks = sharedSubscription.callbacks;
        logWebSocketDebug('rebuilding shared subscription', {
          subscriptionKey,
          stream: sharedSubscription.stream,
          target: sharedSubscription.target,
          callbackCount: callbacks.size,
          options: sharedSubscription.options,
        });
        const nextSubscription = client.subscribe(
          sharedSubscription.stream,
          sharedSubscription.target,
          (data: unknown, action: string) => {
            callbacks.forEach((listener) => {
              listener(data, action);
            });
          },
          sharedSubscription.options,
        );
        sharedSubscriptionsRef.current.set(subscriptionKey, {
          ...sharedSubscription,
          subscription: nextSubscription,
        });
      });

      if (sharedSubscriptionsRef.current.size > 0) {
        addOperationalBreadcrumb({
          category: 'websocket.subscription',
          message: 'Shared subscriptions rebuilt after transport recovery',
          level: 'info',
          data: {
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
            observerCount: observersRef.current.size,
          },
        });
      }
      logWebSocketDebug('rebuildSharedSubscriptions finished', {
        sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        observerCount: observersRef.current.size,
      });
      syncSentryWebSocketContext();
    },
    [logWebSocketDebug, syncSentryWebSocketContext],
  );

  const teardownClient = useCallback(async () => {
    const hadClient = Boolean(clientRef.current);
    const observerCount = observersRef.current.size;
    const sharedSubscriptionCount = sharedSubscriptionsRef.current.size;
    logWebSocketDebug('teardownClient requested', {
      hadClient,
      observerCount,
      sharedSubscriptionCount,
    });
    sharedSubscriptionsRef.current.forEach((sharedSubscription) => {
      sharedSubscription.subscription?.unsubscribe?.();
    });
    addOperationalBreadcrumb({
      category: 'websocket.lifecycle',
      message: 'WebSocket client teardown requested',
      level: 'info',
      data: {
        hadClient,
        observerCount,
        sharedSubscriptionCount,
      },
    });
    sharedSubscriptionsRef.current.clear();
    observersRef.current.clear();
    clearTransportListeners();
    const currentClient = clientRef.current as { close?: (unsubscribe?: boolean) => Promise<void> } | null;
    clientRef.current = null;
    clientTokenRef.current = null;
    if (currentClient?.close) {
      try {
        await currentClient.close();
      } catch {
        // no-op
      }
    }
    const nextState = { isConnected: false, isConnecting: false, error: null };
    setState(nextState);
    syncSentryWebSocketContext(nextState);
    logWebSocketDebug('teardownClient finished', nextState);
  }, [clearTransportListeners, logWebSocketDebug, syncSentryWebSocketContext]);

  const openConnection = useCallback(async (force = false) => {
    const tokenChanged = Boolean(clientRef.current && clientTokenRef.current && clientTokenRef.current !== token);
    const transportConnected = isTransportConnected();
    const shouldPreserveObservers = Boolean(
      clientRef.current
      && (force || tokenChanged)
      && (observersRef.current.size > 0 || sharedSubscriptionsRef.current.size > 0),
    );
    const preservedObservers = shouldPreserveObservers ? new Map(observersRef.current) : null;
    const preservedSharedSubscriptions = shouldPreserveObservers
      ? cloneSharedObserverSubscriptions(sharedSubscriptionsRef.current)
      : null;

    logWebSocketDebug('openConnection invoked', {
      force,
      tokenPresent: Boolean(token),
      tokenChanged,
      transportConnected,
      canOperateInThisTab,
      hasClient: Boolean(clientRef.current),
      isConnecting: state.isConnecting,
      observerCount: observersRef.current.size,
      sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
      shouldPreserveObservers,
    });

    if (state.isConnecting) {
      logWebSocketDebug('openConnection skipped because a connection attempt is already in progress');
      return;
    }

    if (!token) {
      logWebSocketDebug('openConnection aborted because token is missing');
      setState((prev) => ({
        ...prev,
        error: 'Token nao encontrado para WebSocket',
        isConnecting: false,
        isConnected: false,
      }));
      return;
    }

    if (!force && clientRef.current && !tokenChanged && transportConnected) {
      logWebSocketDebug('openConnection skipped because transport is already connected', {
        clientTokenMatchesCurrentToken: clientTokenRef.current === token,
      });
      setState({ isConnected: true, isConnecting: false, error: null });
      return;
    }

    if (clientRef.current) {
      logWebSocketDebug('openConnection tearing down existing client before reconnect', {
        preservedObserverCount: preservedObservers?.size ?? 0,
        preservedSharedSubscriptionCount: preservedSharedSubscriptions?.size ?? 0,
      });
      await teardownClient();
      if (preservedObservers && preservedSharedSubscriptions) {
        observersRef.current = preservedObservers;
        sharedSubscriptionsRef.current = preservedSharedSubscriptions;
        syncSentryWebSocketContext();
        logWebSocketDebug('openConnection restored preserved observers after teardown', {
          restoredObserverCount: observersRef.current.size,
          restoredSharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        });
      }
    }

    setState((prev) => ({ ...prev, isConnecting: true, isConnected: false, error: null }));
    addOperationalBreadcrumb({
      category: 'websocket.lifecycle',
      message: 'Opening WebSocket connection',
      level: 'info',
      data: {
        force,
        tokenChanged,
        transportConnected,
        canOperateInThisTab,
      },
    });

    try {
      const urlWithToken = `${wsUrl}/?token=${token}`;
      const client = dcrfClient.createClient(urlWithToken, {
        websocket: {
          maxReconnectionDelay: 10000,
          minReconnectionDelay: 1000,
          reconnectionDelayGrowFactor: 1.3,
          connectionTimeout: 4000,
          maxRetries: 10,
          debug: import.meta.env.DEV,
        },
      });

      const transport = (client as { transport?: WebSocketTransport }).transport;
      const onConnect = () => {
        logWebSocketDebug('transport connect event received', {
          observerCount: observersRef.current.size,
          sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        });
        rebuildSharedSubscriptions(client);
        addOperationalBreadcrumb({
          category: 'websocket.transport',
          message: 'WebSocket transport connected',
          level: 'info',
          data: {
            observerCount: observersRef.current.size,
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          },
        });
        setState({ isConnected: true, isConnecting: false, error: null });
      };
      const onReconnect = () => {
        logWebSocketDebug('transport reconnect event received', {
          observerCount: observersRef.current.size,
          sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        });
        rebuildSharedSubscriptions(client);
        addOperationalBreadcrumb({
          category: 'websocket.transport',
          message: 'WebSocket transport reconnected',
          level: 'warning',
          data: {
            observerCount: observersRef.current.size,
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          },
        });
        setState({ isConnected: true, isConnecting: false, error: null });
      };
      const onOpen = () => {
        logWebSocketDebug('transport open event received', {
          observerCount: observersRef.current.size,
          sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        });
        rebuildSharedSubscriptions(client);
        addOperationalBreadcrumb({
          category: 'websocket.transport',
          message: 'WebSocket transport opened',
          level: 'info',
          data: {
            observerCount: observersRef.current.size,
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          },
        });
        setState({ isConnected: true, isConnecting: false, error: null });
      };
      if (transport?.on) {
        transport.on('connect', onConnect);
        transport.on('reconnect', onReconnect);
        transport.on('open', onOpen);
        transportListenersRef.current = { transport, onConnect, onReconnect, onOpen };
      }

      client.initialize();
      clientRef.current = client;
      clientTokenRef.current = token;
      const transportIsConnected = transport?.isConnected?.() ?? false;
      logWebSocketDebug('client initialized', {
        transportConnected: transportIsConnected,
        observerCount: observersRef.current.size,
        sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
      });
      if (transportIsConnected) {
        if (preservedSharedSubscriptions?.size) {
          rebuildSharedSubscriptions(client);
        }
        setState({ isConnected: true, isConnecting: false, error: null });
        logWebSocketDebug('client reported connected immediately after initialization', {
          preservedSharedSubscriptionCount: preservedSharedSubscriptions?.size ?? 0,
        });
      }
    } catch (error) {
      logWebSocketDebug('openConnection failed', {
        force,
        tokenChanged,
        canOperateInThisTab,
        observerCount: observersRef.current.size,
        sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        error,
      });
      captureFrontendException(error, {
        level: 'error',
        fingerprint: ['websocket-open-connection-failed'],
        tags: {
          operational_surface: 'websocket',
        },
        contexts: {
          websocket_open: {
            force,
            token_changed: tokenChanged,
            can_operate_in_this_tab: canOperateInThisTab,
            websocket_url_present: Boolean(wsUrl),
          },
        },
      });
      setState({
        isConnected: false,
        isConnecting: false,
        error: 'Falha ao conectar WebSocket',
      });
    }
  }, [canOperateInThisTab, isTransportConnected, logWebSocketDebug, rebuildSharedSubscriptions, state.isConnecting, syncSentryWebSocketContext, teardownClient, token, wsUrl]);

  const connect = useCallback(async () => {
    await openConnection(false);
  }, [openConnection]);

  const disconnect = useCallback(() => {
    void teardownClient();
  }, [teardownClient]);

  const subscribe = useCallback(
    <TData,>(
      stream: string,
      target: string | number | object,
      callback: ObserverCallback<TData>,
      options?: Record<string, unknown>,
    ): WebSocketSubscription => {
      const client = ensureClient();
      const args = typeof target === 'object' && target !== null
        ? target
        : { data: { target } };
      logWebSocketDebug('subscribe called', {
        stream,
        target: args,
        options,
      });
      return client.subscribe(stream, args, callback, {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        ...options,
      });
    },
    [ensureClient, logWebSocketDebug],
  );

  const request = useCallback(
    async <TResponse,>(
      stream: string,
      payload: Record<string, unknown>,
    ): Promise<TResponse> => {
      const client = ensureClient();
      const requestId = payload.request_id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      return client.request(stream, { ...payload, request_id: requestId }) as Promise<TResponse>;
    },
    [ensureClient],
  );

  const paginate = useCallback(
    async <TResponse,>(stream: string, params?: PaginateParams): Promise<TResponse> => {
      return request<TResponse>(stream, {
        action: 'paginate',
        data: {
          pager: {
            page: params?.pager?.page ?? 1,
            pageSize: params?.pager?.pageSize ?? 20,
            reverse: params?.pager?.reverse ?? false,
            useKeyset: params?.pager?.useKeyset ?? false,
            afterCursor: params?.pager?.afterCursor ?? null,
            beforeCursor: params?.pager?.beforeCursor ?? null,
          },
          filters: params?.filters ?? {},
          exclude: params?.exclude ?? {},
          search: params?.search ?? {},
          order: params?.order ?? ['-id'],
        },
      });
    },
    [request],
  );

  const addObserver = useCallback(
    <TData,>(
      stream: string,
      target: string | number | object,
      callback: ObserverCallback<TData>,
      options?: ObserverOptions,
    ) => {
      const client = ensureClient();
      const observerId = `${stream}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const subscriptionKey = getObserverSubscriptionKey(stream, target, options);
      let sharedSubscription = sharedSubscriptionsRef.current.get(subscriptionKey);
      const observerCallback: ObserverCallback = (data, action) => {
        callback(data as TData, action);
      };

      logWebSocketDebug('addObserver called', {
        observerId,
        subscriptionKey,
        stream,
        target,
        options,
        sharedSubscriptionExists: Boolean(sharedSubscription),
        observerCountBefore: observersRef.current.size,
        sharedSubscriptionCountBefore: sharedSubscriptionsRef.current.size,
      });

      if (!sharedSubscription) {
        const callbacks = new Map<string, ObserverCallback>();
        const subscription = client.subscribe(
          stream,
          target,
          (data: unknown, action: string) => {
            callbacks.forEach((listener) => {
              listener(data, action);
            });
          },
          options,
        );
        sharedSubscription = {
          key: subscriptionKey,
          stream,
          target,
          options,
          callbacks,
          subscription,
        };
        sharedSubscriptionsRef.current.set(subscriptionKey, sharedSubscription);
        logWebSocketDebug('created underlying shared observer subscription', {
          observerId,
          subscriptionKey,
          stream,
          target,
          options,
          callbackCount: callbacks.size,
          sharedSubscriptionCountAfter: sharedSubscriptionsRef.current.size,
        });
        addOperationalBreadcrumb({
          category: 'websocket.subscription',
          message: 'Created shared observer subscription',
          level: 'info',
          data: {
            stream,
            observerCount: observersRef.current.size + 1,
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          },
        });
        syncSentryWebSocketContext();
      }

      sharedSubscription.callbacks.set(observerId, observerCallback);
      observersRef.current.set(observerId, {
        id: observerId,
        stream,
        target,
        callback: observerCallback,
        options,
        subscriptionKey,
      });
      syncSentryWebSocketContext();
      logWebSocketDebug('observer registered', {
        observerId,
        subscriptionKey,
        stream,
        observerCountAfter: observersRef.current.size,
        sharedSubscriptionCountAfter: sharedSubscriptionsRef.current.size,
        callbackCount: sharedSubscription.callbacks.size,
      });
      return observerId;
    },
    [ensureClient, logWebSocketDebug, syncSentryWebSocketContext],
  );

  const removeObserver = useCallback((observerId: string) => {
    const observer = observersRef.current.get(observerId);
    if (!observer) {
      logWebSocketDebug('removeObserver skipped because observer was not found', { observerId });
      return;
    }

    logWebSocketDebug('removeObserver called', {
      observerId,
      subscriptionKey: observer.subscriptionKey,
      stream: observer.stream,
      observerCountBefore: observersRef.current.size,
      sharedSubscriptionCountBefore: sharedSubscriptionsRef.current.size,
    });

    const sharedSubscription = sharedSubscriptionsRef.current.get(observer.subscriptionKey);
    if (sharedSubscription) {
      sharedSubscription.callbacks.delete(observerId);
      logWebSocketDebug('observer callback removed from shared subscription', {
        observerId,
        subscriptionKey: observer.subscriptionKey,
        remainingCallbackCount: sharedSubscription.callbacks.size,
      });
      if (sharedSubscription.callbacks.size === 0) {
        sharedSubscription.subscription?.unsubscribe?.();
        sharedSubscriptionsRef.current.delete(observer.subscriptionKey);
        logWebSocketDebug('shared subscription fully removed after last observer cleanup', {
          observerId,
          subscriptionKey: observer.subscriptionKey,
          sharedSubscriptionCountAfter: sharedSubscriptionsRef.current.size,
        });
        addOperationalBreadcrumb({
          category: 'websocket.subscription',
          message: 'Removed shared observer subscription',
          level: 'info',
          data: {
            stream: observer.stream,
            observerCount: observersRef.current.size - 1,
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          },
        });
        syncSentryWebSocketContext();
      }
    }
    observersRef.current.delete(observerId);
    syncSentryWebSocketContext();
    logWebSocketDebug('observer removed', {
      observerId,
      observerCountAfter: observersRef.current.size,
      sharedSubscriptionCountAfter: sharedSubscriptionsRef.current.size,
    });
  }, [logWebSocketDebug, syncSentryWebSocketContext]);

  const clearObservers = useCallback(() => {
    logWebSocketDebug('clearObservers called', {
      observerCountBefore: observersRef.current.size,
      sharedSubscriptionCountBefore: sharedSubscriptionsRef.current.size,
    });
    sharedSubscriptionsRef.current.forEach((sharedSubscription) => {
      sharedSubscription.subscription?.unsubscribe?.();
    });
    sharedSubscriptionsRef.current.clear();
    observersRef.current.clear();
    syncSentryWebSocketContext();
    logWebSocketDebug('clearObservers finished');
  }, [logWebSocketDebug, syncSentryWebSocketContext]);

  useEffect(() => {
    if (!token || !canOperateInThisTab) {
      logWebSocketDebug('token/tab effect requested disconnect', {
        tokenPresent: Boolean(token),
        canOperateInThisTab,
      });
      disconnect();
      return;
    }
    logWebSocketDebug('token/tab effect requested openConnection', {
      tokenPresent: Boolean(token),
      canOperateInThisTab,
      tokenChanged: Boolean(clientRef.current && clientTokenRef.current !== token),
    });
    void openConnection(Boolean(clientRef.current && clientTokenRef.current !== token));
  }, [canOperateInThisTab, disconnect, logWebSocketDebug, openConnection, token]);

  useEffect(() => {
    syncSentryWebSocketContext();
  }, [syncSentryWebSocketContext]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    if (document.visibilityState !== 'visible') {
      markPageBackgrounded('mount:hidden-document');
    }

    const ensureFreshConnection = (reason: string) => {
      const now = Date.now();
      const transportConnected = isTransportConnected();
      const clientTokenMatchesCurrentToken = clientTokenRef.current === token;
      const backgroundedAt = backgroundedAtRef.current;
      const backgroundedReason = backgroundedReasonRef.current;
      const awayForMs = backgroundedAt === null ? 0 : Math.max(0, now - backgroundedAt);
      const forcedRecently = now - lastWakeReconnectAtRef.current < WAKE_UP_FORCE_RECONNECT_DEBOUNCE_MS;
      const shouldForceReconnectAfterBackground = Boolean(
        backgroundedAt !== null
        && !forcedRecently
        && (!transportConnected || !clientTokenMatchesCurrentToken)
      );
      logWebSocketDebug('ensureFreshConnection invoked', {
        reason,
        tokenPresent: Boolean(token),
        canOperateInThisTab,
        documentVisibilityState: document.visibilityState,
        transportConnected,
        clientTokenMatchesCurrentToken,
        backgroundedAt,
        backgroundedReason,
        awayForMs,
        forcedRecently,
        shouldForceReconnectAfterBackground,
        state,
      });
      if (!token || !canOperateInThisTab) {
        logWebSocketDebug('ensureFreshConnection skipped because token/tab preconditions failed', {
          reason,
          tokenPresent: Boolean(token),
          canOperateInThisTab,
        });
        return;
      }
      if (shouldForceReconnectAfterBackground) {
        backgroundedAtRef.current = null;
        backgroundedReasonRef.current = null;
        lastWakeReconnectAtRef.current = now;
        addOperationalBreadcrumb({
          category: 'websocket.lifecycle',
          message: 'Wake-up forcing a fresh WebSocket connection after background resume',
          level: 'info',
          data: {
            reason,
            backgroundedReason,
            awayForMs,
            tokenMatchesClient: clientTokenMatchesCurrentToken,
            transportConnected,
          },
        });
        logWebSocketDebug('ensureFreshConnection forcing a new connection after background resume', {
          reason,
          backgroundedReason,
          awayForMs,
          tokenMatchesClient: clientTokenMatchesCurrentToken,
          transportConnected,
        });
        void openConnection(true);
        return;
      }
      if (transportConnected && clientTokenMatchesCurrentToken) {
        if (backgroundedAtRef.current !== null) {
          backgroundedAtRef.current = null;
          backgroundedReasonRef.current = null;
          logWebSocketDebug('ensureFreshConnection preserved healthy transport after background resume', {
            reason,
            backgroundedReason,
            awayForMs,
            observerCount: observersRef.current.size,
            sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
          });
        }
        if (!state.isConnected) {
          setState({ isConnected: true, isConnecting: false, error: null });
        }
        logWebSocketDebug('ensureFreshConnection skipped because transport already looks healthy', {
          reason,
          state,
          observerCount: observersRef.current.size,
          sharedSubscriptionCount: sharedSubscriptionsRef.current.size,
        });
        return;
      }
      addOperationalBreadcrumb({
        category: 'websocket.lifecycle',
        message: 'Wake-up forcing a fresh WebSocket connection',
        level: 'info',
        data: {
          reason,
          tokenMatchesClient: clientTokenMatchesCurrentToken,
          transportConnected,
        },
      });
      logWebSocketDebug('ensureFreshConnection forcing a new connection', {
        reason,
        tokenMatchesClient: clientTokenMatchesCurrentToken,
        transportConnected,
      });
      void openConnection(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        markPageBackgrounded('visibilitychange:hidden');
        logWebSocketDebug('visibilitychange ignored because document is not visible', {
          documentVisibilityState: document.visibilityState,
        });
        return;
      }
      logWebSocketDebug('visibilitychange detected visible document');
      ensureFreshConnection('visibilitychange:visible');
    };
    const onWindowBlur = () => {
      markPageBackgrounded('window:blur');
    };
    const onWindowFocus = () => {
      logWebSocketDebug('window focus event received');
      ensureFreshConnection('window:focus');
    };
    const onPageHide = () => {
      markPageBackgrounded('window:pagehide');
    };
    const onPageShow = (event: PageTransitionEvent) => {
      logWebSocketDebug('pageshow event received', {
        persisted: event.persisted,
      });
      ensureFreshConnection(event.persisted ? 'window:pageshow:bf-cache' : 'window:pageshow');
    };
    const onOnline = () => {
      logWebSocketDebug('online event received');
      ensureFreshConnection('window:online');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
    };
  }, [canOperateInThisTab, isTransportConnected, logWebSocketDebug, markPageBackgrounded, openConnection, state, token]);

  const value: WebSocketContextType = {
    state,
    client: clientRef.current,
    connect,
    disconnect,
    subscribe,
    request,
    paginate,
    addObserver,
    removeObserver,
    clearObservers,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

/**
 * Access the WebSocket client, state, and helpers.
 */
export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
}
